import { useMemo, useState } from 'react';
import { requiresConfirmation, visibleInputs, type ToolDoc } from '../../agents/toolCatalog.ts';
import PanelEmpty from './PanelEmpty.tsx';
import './ToolsCatalog.css';

/**
 * A→Z by tool name. Names are ASCII `lower_snake_case`, so a codepoint compare
 * is deterministic everywhere — no dependence on the runtime's locale data — and
 * it keeps each prefixed family (`doc_*`, `stock_*`, `workflow_*`) contiguous.
 */
function byName(a: ToolDoc, b: ToolDoc): number {
  if (a.name === b.name) {
    return 0;
  }
  return a.name < b.name ? -1 : 1;
}

interface ToolsCatalogProps {
  /** The tools the agent can call, with user-facing copy. */
  tools: readonly ToolDoc[];
  /** Guest console (tiers T8): rows without `guestEnabled` render locked. */
  guest?: boolean;
}

/**
 * Inspector "Tools" section, "All" scope: the catalog of tools the agent can
 * call, each row expanding to a plain-English description and its inputs.
 * Complements the conversation-scoped tool-use log. Confirmation parameters
 * (e.g. a destructive tool's `confirmed`) are filtered out of the inputs and
 * surfaced as a "requires confirmation" flag instead. The section header + scope
 * toggle live in the inspector accordion, so this renders body content only.
 *
 * Rows render alphabetically by tool name, whatever order the caller supplies.
 * The manifest lists tools in the agents' declaration order, which reads as
 * arbitrary to a user scanning ~50 rows for one name.
 */
export default function ToolsCatalog({ tools, guest = false }: ToolsCatalogProps) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const sorted = useMemo(() => [...tools].sort(byName), [tools]);

  const toggle = (name: string): void => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  if (tools.length === 0) {
    return (
      <section className="om-toolcat om-toolcat--empty">
        <PanelEmpty>The tools this agent can use will be listed here.</PanelEmpty>
      </section>
    );
  }

  return (
    <section className="om-toolcat">
      {sorted.map((tool) => {
        const isOpen = open.has(tool.name);
        const inputs = visibleInputs(tool);
        const locked = guest && tool.guestEnabled !== true;
        return (
          <div
            key={tool.name}
            className={`om-tcrow${isOpen ? ' om-tcrow--open' : ''}${locked ? ' om-tcrow--locked' : ''}`}
          >
            <button
              type="button"
              className="om-tcrow__toggle"
              aria-expanded={isOpen}
              onClick={() => toggle(tool.name)}
            >
              <span className="om-tcrow__ic" aria-hidden="true">
                ⚙
              </span>
              <span className="om-tcrow__main">
                <span className="om-tcrow__name">
                  {tool.name}
                  {locked ? (
                    <span className="om-tcrow__locked" title="Available with a free account">
                      🔒 account
                    </span>
                  ) : null}
                  {requiresConfirmation(tool) ? (
                    <span
                      className="om-tcrow__confirm"
                      title="The agent asks you to confirm before running this tool"
                    >
                      requires confirmation
                    </span>
                  ) : null}
                </span>
                <span className="om-tcrow__summary">{tool.summary}</span>
              </span>
              <span className="om-tcrow__chevron" aria-hidden="true">
                {isOpen ? '▾' : '▸'}
              </span>
            </button>
            {isOpen ? (
              <div className="om-tcrow__detail">
                <p className="om-tcrow__desc">{tool.description}</p>
                {inputs.length > 0 ? (
                  <div className="om-tcrow__section">
                    <div className="om-tcrow__label">inputs</div>
                    <div className="om-tcinputs">
                      {inputs.map((input) => (
                        <div key={input.name} className="om-tcinput">
                          <span className="om-tcinput__name">
                            {input.name}
                            {input.required ? (
                              <span className="om-tcinput__req">required</span>
                            ) : null}
                          </span>
                          <span className="om-tcinput__desc">{input.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
